import type { FeedItemPhoto } from '@/types/feed';
import { imageUrl } from '@/utils/image-url';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useState } from 'react';
import {
    Image,
    Modal,
    Platform,
    Pressable,
    Text,
    View
} from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

interface ImageViewerProps {
  photos: FeedItemPhoto[];
  initialIndex: number;
  visible: boolean;
  onClose: () => void;
}

export function ImageViewer({
  photos,
  initialIndex,
  visible,
  onClose,
}: ImageViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
    }
  }, [visible, initialIndex]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => Math.min(photos.length - 1, prev + 1));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, photos.length, onClose]);

  if (!visible || photos.length === 0) return null;

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < photos.length - 1;

  return (
    <Modal
      transparent
      visible={visible}
      onRequestClose={onClose}
      statusBarTranslucent
      animationType="none"
    >
      <Animated.View
        entering={FadeIn.duration(200)}
        exiting={FadeOut.duration(150)}
        style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
        }}
      >
        {/* Header: close button + counter */}
        <View
          className="flex-row items-center justify-between px-4"
          style={{ paddingTop: Platform.OS === 'web' ? 16 : 56 }}
        >
          <View style={{ width: 44 }} />
          {photos.length > 1 && (
            <Text className="text-white text-sm font-medium">
              {currentIndex + 1} / {photos.length}
            </Text>
          )}
          <Pressable
            onPress={onClose}
            className="w-11 h-11 items-center justify-center rounded-full"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <Ionicons name="close" size={22} color="#fff" />
          </Pressable>
        </View>

        {/* Image area with navigation */}
        <View className="flex-1 justify-center items-center">
          {/* Backdrop press to close */}
          <Pressable
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onPress={onClose}
          />

          {/* Current photo */}
          <Image
            source={{ uri: imageUrl(photos[currentIndex].r2Key || photos[currentIndex].imageUrl, 'full') }}
            style={{ width: '90%', height: '80%' }}
            resizeMode="contain"
            accessibilityLabel={`Photo ${currentIndex + 1} of ${photos.length}`}
          />

          {/* Left arrow */}
          {canGoPrev && (
            <Pressable
              onPress={() => setCurrentIndex((prev) => prev - 1)}
              style={{
                position: 'absolute',
                left: 12,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 22,
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              accessibilityRole="button"
              accessibilityLabel="Previous photo"
            >
              <Ionicons name="chevron-back" size={24} color="#fff" />
            </Pressable>
          )}

          {/* Right arrow */}
          {canGoNext && (
            <Pressable
              onPress={() => setCurrentIndex((prev) => prev + 1)}
              style={{
                position: 'absolute',
                right: 12,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 22,
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              accessibilityRole="button"
              accessibilityLabel="Next photo"
            >
              <Ionicons name="chevron-forward" size={24} color="#fff" />
            </Pressable>
          )}
        </View>
      </Animated.View>
    </Modal>
  );
}
